import { Component, Prop, SimpleEvent } from '@eastsideco/salvo-ts';
import { ThemeComponent } from '@/theme/ThemeComponent';
// eslint-disable-next-line max-len
import Swiper, { Navigation, Pagination, Scrollbar, Autoplay, Thumbs, Controller, SwiperOptions, Mousewheel, EffectFade } from 'swiper';
import 'swiper/css/bundle';
import { ProductOptionPicker } from '@/components/Product/ProductOptionPicker';

import PhotoSwipeLightbox from 'photoswipe/lightbox';
import 'photoswipe/style.css';

@Component
export class EastSlider extends ThemeComponent {
    @Prop({ default: false }) navigation!: boolean;
    @Prop({ default: '' }) svg!: string;
    @Prop({ default: false }) pagination!: boolean;
    @Prop({ default: false }) clickablePagination!: boolean;
    @Prop({ default: false }) scrollbar!: boolean;
    @Prop({ default: null }) breakpoint!: number;
    @Prop({ default: null }) thumbnails!: string;
    @Prop({ default: false }) isThumbnailSlider!: boolean;
    @Prop({ default: false }) listenToVariantChange!: boolean;
    @Prop({ default: false }) usePhotoswipe!: boolean;
    @Prop({ default: false }) ignoreOneSlideInViewClass!: boolean;
    @Prop({ default: -1 }) colorOptionIndex!: number;
    @Prop() settings!: SwiperOptions;

    private _onSwiperInit = new SimpleEvent<Swiper>();
    get onSwiperInit() {
        return this._onSwiperInit.asEvent();
    }

    init() {
        // Keep track of swiper instances
        let slider: Swiper;

        // Set default nav icon
        this.setDefaultNavIcon();

        // Add required classes to make slider work
        this.addSliderClasses();

        // Insert styling wrapper div to DOM
        this.addSlides();

        // Add elements if props defined
        if (this.navigation || this.pagination || this.scrollbar) {
            this.checkPropsAddElements();
        }

        // Init photoswipe
        if (this.usePhotoswipe) {
            this.initPhotoswipe();
        }

        // Check responsive breakpoint
        if (this.breakpoint !== null) {

            // Slider should init below our breakpoint
            const mmBreakpoint = window.matchMedia(`(min-width: ${this.breakpoint + 1}px)`);

            // Check breakpoint, either init or destroy slider
            const breakpointChecker = () => {
                if (mmBreakpoint.matches) {
                    if (slider !== undefined) {
                        slider.destroy(true, true);
                    }
                    return;
                } else {
                    slider = this.initialiseSwiper();
                }
            };

            // Init listener and initial check
            breakpointChecker();
            mmBreakpoint.addListener(breakpointChecker);
            return;
        }

        // Check if this slider is a nav for another
        if (this.thumbnails !== null) {
            const parentSliderElement = document.querySelector(`${this.thumbnails}`);
            if (parentSliderElement && parentSliderElement.tagName === 'EAST-SLIDER') {
                const parentSliderEastSliderComponent = parentSliderElement as EastSlider;
                this.$subscribe(parentSliderEastSliderComponent.onSwiperInit, (parentSliderSwiperComponent: Swiper) => {

                    // Update swiper settings object
                    this.settings.thumbs = { swiper: parentSliderSwiperComponent };

                    // Initialise swiper
                    slider = this.initialiseSwiper();
                });
            }
        } else {
            // Initialise swiper
            slider = this.initialiseSwiper();
        }

        // Listen for variant change?
        if (this.listenToVariantChange) {
            const productOptionPickers = document.querySelectorAll('#product-form-option-picker');

            for (const productOptionPicker of productOptionPickers) {
                if (productOptionPicker && productOptionPicker.tagName === 'PRODUCT-OPTION-PICKER') {
                    const productOptionPickerComponent = productOptionPicker as ProductOptionPicker;

                    this.$subscribe(
                        productOptionPickerComponent.onSelectedVariantChange,
                        (variant: Shopify.Liquid.Variant | null) => {
                            const imageId = variant?.featured_image ? variant.featured_image.id : null;
                            this.handleOnVariantchange(slider, variant, imageId);
                        }
                    );
                }
            }
        }

    }

    handleOnVariantchange(slider: Swiper, variant: Shopify.Liquid.Variant | null, imageId: number | null) {

        // Only continue if we have variant data
        if (variant === null) {
            return;
        }

        // Define variables
        let matchingSlideIndex = null;

        // Find slide by image ID
        if (imageId !== null) {
            const slides = slider.el.querySelectorAll('.swiper-slide');
            let slideIndex = 0;
            for (const slide of slides) {
                const slideAttributeImageId = slide.getAttribute('data-image-id');
                if (slideAttributeImageId !== null && slideAttributeImageId !== '') {
                    const slideImageId = parseInt(slideAttributeImageId, 10);
                    if (slideImageId == imageId) {
                        matchingSlideIndex = slideIndex;
                    }
                }
                slideIndex++;
            }
        }

        // Find slide by image alt (colour only)
        if (matchingSlideIndex === null && this.colorOptionIndex !== -1) {

            // Get color
            if (this.colorOptionIndex < 1 || this.colorOptionIndex > 3) {
                throw new Error(`Invalid color option index: ${this.colorOptionIndex}`);
            }
            const variantOptionKey = `option${this.colorOptionIndex}` as ShopifyProductOptionKey;
            const color = variant[variantOptionKey] ? variant[variantOptionKey].toLowerCase() : null;

            // Find slide index
            const sliderImages = slider.el.querySelectorAll('.swiper-slide img');
            let slideIndex = 0;
            for (const image of sliderImages) {
                const thisImage = image as HTMLImageElement;
                const alt = thisImage.alt.toLowerCase();
                if (alt === color) {
                    matchingSlideIndex = slideIndex;
                }
                slideIndex++;
            }
        }

        // Go to slide
        if (matchingSlideIndex !== null) {
            slider.slideTo(matchingSlideIndex, 300);
        }
    }

    setDefaultNavIcon() {
        if (!this.svg && this.navigation) {
            this.svg = `
                <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    width="28" 
                    height="52" 
                    viewBox="0 0 28 52">
                    <g><g>
                    <path 
                        fill="none" 
                        stroke="#000" 
                        stroke-linecap="round" 
                        stroke-linejoin="round" 
                        stroke-miterlimit="20" 
                        stroke-width="3" 
                        d="M2.5 26.5l23 23m0-47l-23 24"/>
                    </g></g>
                </svg>
            `;
        }
    }

    addSliderClasses() {
        this.classList.add('swiper');
        if (this.navigation) {
            this.classList.add('swiper--has-navigation');
        }
        if (this.pagination) {
            this.classList.add('swiper--has-pagination');
        }
        if (this.settings.slidesPerView && this.settings.slidesPerView === 1 && !this.ignoreOneSlideInViewClass) {
            this.classList.add('swiper--one-slide-in-view');
        }
        const children = this.children;
        for (const child of children) {
            if (!child.classList.contains('swiper-slide')) {
                child.classList.add('swiper-slide');
            }
        }
    }

    addSlides() {
        const wrapper = document.createElement('div');
        wrapper.className = 'swiper-wrapper';
        for (const child of Array.from(this.children)) {
            wrapper.appendChild(child);
        }
        this.appendChild(wrapper);
    }

    checkPropsAddElements() {
        // Add navigation
        if (this.navigation) {
            const nextButton = document.createElement('button');
            nextButton.setAttribute('role', 'button');
            nextButton.setAttribute('type', 'button');
            nextButton.classList.add('swiper-next');
            nextButton.classList.add('swiper-navigation');
            nextButton.setAttribute('aria-label', 'Previous slide');
            nextButton.innerHTML = this.svg;

            const prevButton = document.createElement('button');
            prevButton.setAttribute('role', 'button');
            prevButton.setAttribute('type', 'button');
            prevButton.classList.add('swiper-prev');
            prevButton.classList.add('swiper-navigation');
            prevButton.setAttribute('aria-label', 'Next slide');
            prevButton.innerHTML = this.svg;

            this.appendChild(nextButton);
            this.appendChild(prevButton);

            this.settings.navigation = { nextEl: '.swiper-next', prevEl: '.swiper-prev' };
        }

        // Add pagination and assign type
        if (this.pagination || this.settings.pagination) {
            const paginationElem = document.createElement('div');
            paginationElem.classList.add('swiper-pagination');
            this.appendChild(paginationElem);
            const pagination = (typeof this.settings.pagination == 'object')
                ? this.settings.pagination.type
                : 'bullets';
            this.settings.pagination = {
                el: '.swiper-pagination',
                type: pagination,
                clickable: this.clickablePagination
            };
        }

        // Add scrollbar
        if (this.scrollbar || this.settings.scrollbar) {
            const scrollbarElem = document.createElement('div');
            scrollbarElem.classList.add('swiper-scrollbar');
            this.appendChild(scrollbarElem);
            this.settings.scrollbar = {
                el: '.swiper-scrollbar',
                draggable: true,
                hide: false
            };
        }
    }

    initPhotoswipe() {
        const lightbox = new PhotoSwipeLightbox({
            gallery: '.product-gallery',
            children: 'a',
            pswpModule: () => import('photoswipe')
        });
        lightbox.init();
    }

    initialiseSwiper() {
        Swiper.use([Navigation, Pagination, Scrollbar, Autoplay, Thumbs, Controller, Mousewheel, EffectFade]);
        const slider = new Swiper(this, this.settings);

        // Dispatch event
        this._onSwiperInit.dispatch(slider);

        // Add keyboard accessibility
        this.addKeyboardAccessibility(slider);

        // Force allow clicks for thumbanil slides
        if (this.isThumbnailSlider) {
            slider.on('touchEnd', (s1: Swiper, e: unknown) => {
                // Swiper's typings are incorrect - missing allowClick
                // allowClick is an unsupported attribute, used to fix the issue of
                // thumbnail slider clicks not always changing the active slide index
                // issue noted more here: https://github.com/nolimits4web/swiper/issues/1623
                const s = s1 as Swiper & { allowClick: boolean };
                s.allowClick = true;
            });
        }

        // Ensure correct src is used if pre-load component exists
        const images = this.querySelectorAll('img[data-preload]');
        for (const image of images) {
            if (image.getAttribute('data-preload') !== 'loaded') {
                const src = image.getAttribute('data-preload')
                    ? image.getAttribute('data-preload')
                    : image.getAttribute('src');
                if (src) {
                    image.setAttribute('src', src);
                    image.setAttribute('data-preload', 'loaded');
                }
            }
        }

        // Return slider
        return slider;
    }

    addKeyboardAccessibility(slider: Swiper) {
        // Make slides focusable and handle focus events
        const slides = this.querySelectorAll('.swiper-slide');
        for (const slide of slides) {
            const slideElement = slide as HTMLElement;
            slideElement.setAttribute('tabindex', '0');
            slideElement.setAttribute('role', 'tab');

            // Add focus event listener to make slide active when focused
            slideElement.addEventListener('focus', () => {
                const slideIndex = Array.from(slider.slides).indexOf(slideElement);
                if (slideIndex !== -1 && slideIndex !== slider.activeIndex) {
                    slider.slideTo(slideIndex);
                }
            });

            // Add keyboard navigation
            slideElement.addEventListener('keydown', (event: KeyboardEvent) => {
                switch (event.key) {
                    case 'ArrowLeft': {
                        event.preventDefault();
                        slider.slidePrev();
                        // Focus the new active slide
                        const newActiveSlide = slider.slides[slider.activeIndex] as HTMLElement;
                        if (newActiveSlide) {
                            newActiveSlide.focus();
                        }
                        break;
                    }
                    case 'ArrowRight': {
                        event.preventDefault();
                        slider.slideNext();
                        // Focus the new active slide
                        const newActiveSlide = slider.slides[slider.activeIndex] as HTMLElement;
                        if (newActiveSlide) {
                            newActiveSlide.focus();
                        }
                        break;
                    }
                    case 'Home': {
                        event.preventDefault();
                        slider.slideTo(0);
                        const firstSlide = slider.slides[0] as HTMLElement;
                        if (firstSlide) {
                            firstSlide.focus();
                        }
                        break;
                    }
                    case 'End': {
                        event.preventDefault();
                        slider.slideTo(slider.slides.length - 1);
                        const lastSlide = slider.slides[slider.slides.length - 1] as HTMLElement;
                        if (lastSlide) {
                            lastSlide.focus();
                        }
                        break;
                    }
                }
            });
        }

        // Update aria-selected states when slides change
        slider.on('slideChange', () => {
            for (const [index, slide] of Array.from(slider.slides).entries()) {
                const slideElement = slide as HTMLElement;
                const isActive = index === slider.activeIndex;
                slideElement.setAttribute('aria-selected', isActive.toString());
            }
        });

        // Prevent layout shifts when focus moves outside the slider
        this.addEventListener('focusout', (event: FocusEvent) => {
            // Only prevent default if focus is moving outside the slider component
            const relatedTarget = event.relatedTarget as HTMLElement;
            if (relatedTarget && !this.contains(relatedTarget)) {
                // Allow the focus to move naturally without triggering any slider updates
                event.stopPropagation();
            }
        });

        // Handle Shift+Tab navigation specifically to prevent layout shifts
        this.addEventListener('keydown', (event: KeyboardEvent) => {
            if (event.key === 'Tab' && event.shiftKey) {
                // When Shift+Tab is pressed, ensure no slider updates occur
                // This prevents layout shifts when navigating backwards
                const activeElement = document.activeElement as HTMLElement;
                if (activeElement && this.contains(activeElement)) {
                    // Allow the natural focus flow without any interference
                    event.stopPropagation();
                }
            }
        });

        // Ensure navigation buttons don't cause layout shifts
        const navigationButtons = this.querySelectorAll('.swiper-navigation');
        for (const button of navigationButtons) {
            const buttonElement = button as HTMLElement;

            // Prevent focusout events from triggering slider updates
            buttonElement.addEventListener('focusout', (event: FocusEvent) => {
                // Allow focus to move naturally without any slider interference
                event.stopPropagation();
            });

            // Ensure navigation buttons don't cause layout shifts when focused/blurred
            buttonElement.addEventListener('focus', (event: FocusEvent) => {
                // Prevent any layout changes when navigation is focused
                event.stopPropagation();
            });

            buttonElement.addEventListener('blur', (event: FocusEvent) => {
                // Prevent any layout changes when navigation loses focus
                event.stopPropagation();
            });
        }
    }
}
